import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useSortable } from '@dnd-kit/sortable';
import Icon, { COLORS, ICONS } from '../icon';
import { SUPPORTED_CHAT_FILE_FORMATS } from '../../../types/constants';
import GRInput from './gr-input';
import { GreenroomState as GS } from '../../../stores/types/types';
import { UploadDocument } from '../../../connection/upload-document';
import { showAlert } from '../alert-service';
import { Field, maxPictureCount } from '../../right-panel/settings/content-generator-handler';
import { ContentTemplate } from '../../../stores/reducers/generate-content';

interface GrPictureInputProps {
	onChange: (event: any) => void;
	onBlur?: () => void;
	onFocus?: () => void;
	addPicture?: (field: Field) => void;
	removePicture?: (field: Field, count: number) => void;
	values?: Record<string, string>;
	count?: number;
	totalCount?: number;
	defaultValues?: Record<string, string | object> | undefined;
	draggingCount?: number;
	draggingOver?: number;
	template: ContentTemplate;
	field: Field
}

// eslint-disable-next-line no-template-curly-in-string
export const getNumberedFieldName = (name: string, count: number) => name.replace('${i}', count.toString());

export const getUserFriendlyPictureName = (possiblePictureName: string | undefined) => {
	if (possiblePictureName && !possiblePictureName?.includes('/title_templates')) {
		const parts = possiblePictureName?.split('/');
		if (parts.length > 1) {
			return parts[parts.length - 1]?.substring(11) ?? 'preview.png';
		}
	}
	return 'preview.png';
};

export default function GrPictureInput(props: GrPictureInputProps) {
	const {
		onChange,
		onBlur = () => {},
		onFocus = () => {},
		addPicture = () => {},
		removePicture = () => {},
		values,
		count = 1,
		totalCount = 1,
		defaultValues,
		draggingCount = 0,
		draggingOver = 0,
		template,
		field,
	} = props;

	const {
		userData: fieldName,
		options,
		variables = [],
		name: label = '',
	} = field;

	const {
		attributes,
		listeners,
		setNodeRef,
	} = useSortable({
		id: getNumberedFieldName(fieldName, count),
	});

	const [pictureName, setPictureName] = React.useState('preview.png');
	const file = useRef<HTMLInputElement | null>(null);
	const image = useRef<HTMLDivElement | null>(null);
	const token = useSelector((s: GS) => s.SessionReducer.token);

	useEffect(() => {
		setPictureName(getUserFriendlyPictureName(values?.[getNumberedFieldName(fieldName, count)]));
	}, [values]);

	const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files) {
			try {
				const result = await UploadDocument(token, event.target.files);
				if (result) {
					setPictureName(result.originalName);
					if (image.current?.style) {
						image.current.style.backgroundImage = `url(${result.location})`;
					}
				}
				onChange({
					target: {
						name: getNumberedFieldName(fieldName, count),
						value: result.location,
					},
				});
			} catch (error) {
				showAlert({
					message: 'Upload Error',
					description: 'We ran into an issue uploading that document. Please try again',
					duration: 4000,
				});
				console.warn(error);
			}
		}

		if (file?.current?.value) {
			file.current.value = '';
		}
	};

	const isUploadedPicture = pictureName !== 'preview.png';
	const iconToUseForAddButton = defaultValues?.['button-icon'] && typeof defaultValues?.['button-icon'] === 'string'
		? ICONS[defaultValues['button-icon'] as keyof typeof ICONS]
		: undefined;

	if (draggingCount === count) {
		return (
			<>
				{draggingOver !== 0 && (
					<></>
				)}
				{draggingOver === 0 && (
					<div
						className='gr-input-picture'
						key={`picture-${count}`}
						id={`picture-${count}`}
					>
						{count > 1 && (
							<hr className="header-line" />
						)}
						<div className="drag-holder">
						</div>
					</div>
				)}
			</>
		);
	}

	return (
		<div
			className={'gr-input-picture'}
			key={`picture-${count}`}
			id={`picture-${count}`}
			ref={setNodeRef}
			{...attributes}
		>
			{!!defaultValues?.oneToManyLabel && (
				<>
					{count > 1 && (
						<hr className="header-line" />
					)}
					{draggingOver === count && draggingCount > count && (
						<div className="dragging-over under">
							<svg xmlns="http://www.w3.org/2000/svg" width="395" height="42" viewBox="0 0 395 42" fill="none">
								<g filter="url(#filter0_d_25759_94984)">
									<path d="M21 21H374" stroke="#A6FF79" stroke-linecap="round"/>
								</g>
								<defs>
									<filter id="filter0_d_25759_94984" x="0.5" y="0.5" width="394" height="41" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
										<feFlood flood-opacity="0" result="BackgroundImageFix"/>
										<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
										<feOffset/>
										<feGaussianBlur stdDeviation="10"/>
										<feComposite in2="hardAlpha" operator="out"/>
										<feColorMatrix type="matrix" values="0 0 0 0 0.65098 0 0 0 0 1 0 0 0 0 0.47451 0 0 0 1 0"/>
										<feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_25759_94984"/>
										<feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_25759_94984" result="shape"/>
									</filter>
								</defs>
							</svg>
						</div>
					)}
					<div className="gr-input-picture-label">
						<div
							className="gr-input-picture-drag"
							{...listeners}
						>
							<div
								className="gr-input-picture-label-drag"
							>
								<Icon
									name={ICONS.DRAG}
									size={12}
									color={COLORS.TEXT_GRAY_500}
								/>
							</div>
						</div>
						<>
							{defaultValues?.oneToManyLabel ?? 'Picture'}
							&nbsp;
							{count}
						</>
						<div
							className="gr-input-picture-delete-button"
							onClick={() => removePicture(field, count)}
						>
							Delete
						</div>
					</div>
				</>
			)}
			{!!label && (
				<div className="gr-input-picture-description-label">
					{label}
				</div>
			)}
			<div className="gr-input-picture-fields">
				<input
					type="file"
					style={{ display: 'none' }}
					ref={file}
					onChange={handleUpload}
					accept={SUPPORTED_CHAT_FILE_FORMATS.PICTURE_MIME_TYPES.toString()}
				/>
				{variables.map((variable: any) => {
					const subFieldName = getNumberedFieldName(variable.userData, count);
					const backUpFieldName = getNumberedFieldName(variable.userData, 2);

					let value = values?.[fieldName] ?? '';
					if (
						!value
						&& value !== ''
						&& values?.[subFieldName]
						&& count >= 2
						&& typeof defaultValues?.[backUpFieldName] === 'string'
					) {
						value = defaultValues?.[backUpFieldName] as string;
					}

					return (
						<GRInput
							key={variable.name}
							fieldName={getNumberedFieldName(variable.userData, count)}
							friendlyName={variable.name}
							fieldType={variable.type}
							onChange={onChange}
							options={options}
							value={value ?? ''}
							onFocus={onFocus}
							onBlur={onBlur}
						/>
					);
				})}
				{template.type === 'brandlive-walkups' && totalCount === 1 && (
					<GRInput
						key="eventName"
						fieldName="eventName"
						friendlyName="Event Name"
						fieldType={'text'}
						onChange={onChange}
						options={options}
						value={values?.eventName ?? 'Brandlive Studios'}
						onFocus={onFocus}
						onBlur={onBlur}
					/>
				)}
			</div>
			<div className="gr-input-picture-row">
				<div
					className="gr-input-picture-thumbnail"
					style={{
						backgroundImage: `url(${
							values?.[getNumberedFieldName(fieldName, count)]
							?? defaultValues?.[getNumberedFieldName(fieldName, 2)]
							?? ''
						})`,
					}}
					ref={image}
				>
					&nbsp;
				</div>
				<div className="gr-input-picture-name">
					{pictureName}
				</div>
				<div
					className="gr-input-picture-button"
					onClick={() => {
						file?.current?.click();
					}}
				>
					{isUploadedPicture ? 'Replace' : 'Add Image'}
					<Icon
						name={ICONS.CARROT_DOWN}
						size={12}
						color={COLORS.TEXT_GRAY_200}
					/>
				</div>
			</div>
			{draggingOver === count && draggingCount < count && (
				<div className="dragging-over">
					<svg xmlns="http://www.w3.org/2000/svg" width="395" height="42" viewBox="0 0 395 42" fill="none">
						<g filter="url(#filter0_d_25759_94984)">
							<path d="M21 21H374" stroke="#A6FF79" stroke-linecap="round"/>
						</g>
						<defs>
							<filter id="filter0_d_25759_94984" x="0.5" y="0.5" width="394" height="41" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
								<feFlood flood-opacity="0" result="BackgroundImageFix"/>
								<feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
								<feOffset/>
								<feGaussianBlur stdDeviation="10"/>
								<feComposite in2="hardAlpha" operator="out"/>
								<feColorMatrix type="matrix" values="0 0 0 0 0.65098 0 0 0 0 1 0 0 0 0 0.47451 0 0 0 1 0"/>
								<feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_25759_94984"/>
								<feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_25759_94984" result="shape"/>
							</filter>
						</defs>
					</svg>
				</div>
			)}
			{defaultValues?.oneToManyField === fieldName
				&& iconToUseForAddButton
				&& count === 1
				&& totalCount < maxPictureCount
				&& (<div className="gr-input-add-button" onClick={() => addPicture(field)}>
					<>
						{!!defaultValues['button-icon'] && (
							<Icon
								name={iconToUseForAddButton}
								size={12}
								color={COLORS.TEXT_GRAY_200}
							/>
						)}
						{defaultValues['button-text'] ? defaultValues['button-text'] : 'Add'}
					</>
				</div>)
			}
		</div>
	);
}
